import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import {
  Bot,
  User,
  Send,
  X,
  Calendar,
  UserCheck,
  Mail,
  Phone,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { fetchWithSchema } from '@/lib/api';
import { getSchemaScopedStorageKey } from '@/lib/schemaStorage';

interface ChatMessage {
  id: string;
  type: 'bot' | 'user';
  content: string;
  timestamp: Date;
}

interface AppointmentData {
  service?: string;
  specialist?: string;
  name?: string;
  email?: string;
  phone?: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  created_at?: string;
}

interface AppointmentContext {
  employees: Array<{
    id: number;
    name: string;
    surname: string;
    specialty?: string;
    services: Array<{
      id: number;
      name: string;
      category: string;
      price: number;
      duration: number;
    }>;
    availability: Array<{
      dayOfWeek: number;
      startTime: string;
      endTime: string;
    }>;
  }>;
  services: Array<{
    id: number;
    name: string;
    description: string;
    category: string;
    price: number;
    duration: number;
    active: boolean;
  }>;
}

interface ChatbotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAppointmentComplete: (data: AppointmentData) => void;
}

const ChatbotModal: React.FC<ChatbotModalProps> = ({
  isOpen,
  onClose,
  onAppointmentComplete,
}) => {
  const { t } = useTranslation();
  const { settings } = useAppSettings();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [appointmentData, setAppointmentData] = useState<AppointmentData & {
    serviceId?: number;
    employeeId?: number;
    date?: string;
  }>({});
  const [appointmentContext, setAppointmentContext] = useState<AppointmentContext | null>(null);
  const [contextLoading, setContextLoading] = useState(false);

  // Helper function to get day name and calculate actual date
  const getDayInfo = (dayIndex: number) => {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + dayIndex);

    const dayNames = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];
    const dayName = dayNames[targetDate.getDay()];

    // Format date as YYYY-MM-DD for backend
    const formattedDate = targetDate.toISOString().split('T')[0];

    return {
      dayName,
      fullDate: formattedDate,
      dateObj: targetDate
    };
  };

  // Helper function to get the next occurrence of a specific day of the week
  const getNextDayOfWeek = (targetDayOfWeek: number) => {
    const today = new Date();
    const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Calculate days until next target day
    let daysUntilTarget = targetDayOfWeek - currentDayOfWeek;
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7; // Next week
    }

    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysUntilTarget);

    const dayNames = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];
    const dayName = dayNames[targetDate.getDay()];

    // Format date as YYYY-MM-DD for backend
    const formattedDate = targetDate.toISOString().split('T')[0];

    return {
      dayName,
      fullDate: formattedDate,
      dateObj: targetDate,
      daysUntil: daysUntilTarget
    };
  };

  // Load appointment data from sessionStorage on mount
  const appointmentDataStorageKey = getSchemaScopedStorageKey('appointmentData');
  useEffect(() => {
    const savedData = sessionStorage.getItem(appointmentDataStorageKey);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);

        // Validate that parsed data has the expected structure
        if (typeof parsedData === 'object' && parsedData !== null) {
          // Ensure created_at is set if loading from sessionStorage
          if (!parsedData.created_at) {
            parsedData.created_at = new Date().toISOString();
          }

          setAppointmentData(parsedData);
        } else {
          console.warn('Invalid data structure in sessionStorage, skipping load');
        }
      } catch (error) {
        console.error('Error loading appointment data from sessionStorage:', error);
      }
    }
  }, []);



  // Save appointment data to sessionStorage whenever it changes
  useEffect(() => {
    if (Object.keys(appointmentData).length > 0) {
      // Clean data before saving to sessionStorage (but preserve IDs)
      const cleaned = { ...appointmentData };

      // Ensure created_at is set if not already
      if (!cleaned.created_at) {
        cleaned.created_at = new Date().toISOString();
      }

      if (cleaned.service) {
        cleaned.service = cleaned.service
          .replace(/^\*\*\s*/, '')
          .replace(/\s*\([^)]*\)$/, '')
          .trim();
      }

      if (cleaned.specialist) {
        cleaned.specialist = cleaned.specialist
          .replace(/^\*\*\s*/, '')
          .replace(/^\*\s*/, '')
          .trim();
      }

      sessionStorage.setItem(appointmentDataStorageKey, JSON.stringify(cleaned));
    }
  }, [appointmentData]);

  // Helper function to get complete appointment data with IDs
  const getCompleteAppointmentData = () => {
    const data = { ...appointmentData };

    // Clean names but keep IDs
    if (data.service) {
      data.service = data.service
        .replace(/^\*\*\s*/, '')
        .replace(/\s*\([^)]*\)$/, '')
        .trim();
    }
    if (data.specialist) {
      data.specialist = data.specialist
        .replace(/^\*\*\s*/, '')
        .replace(/^\*\s*/, '')
        .trim();
    }

    return data;
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load appointment context from backend
  const loadAppointmentContext = async () => {
    try {
      setContextLoading(true);
      const response = await fetchWithSchema('/api/appointment-assistant/context');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const context = await response.json();

      if (context.success) {
        setAppointmentContext(context.data);
      } else {
        console.error('Failed to load context:', context.error);
      }
    } catch (error) {
      console.error('❌ FRONTEND: Error loading appointment context:', error);
    } finally {
      setContextLoading(false);
    }
  };

  // Create appointment in database
  const createAppointmentInDatabase = async (data: any) => {
    try {
      // Prepare data for backend - send IDs and required fields
      const backendData = {
        serviceId: data.serviceId,
        employeeId: data.employeeId,
        date: data.date, // This should be in "Ziua X, HH:MM - HH:MM" format
        name: data.name?.trim(),
        email: data.email?.trim(),
        phone: data.phone?.trim(),
        service: data.service?.replace(/^\*\*\s*/, '').replace(/\s*\([^)]*\)$/, '').trim(),
        specialist: data.specialist?.replace(/^\*\*\s*/, '').replace(/^\*\s*/, '').trim(),
      };

      const response = await fetchWithSchema('/api/appointment-assistant/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to create appointment:', errorData);
        throw new Error(errorData.error || 'Failed to create appointment');
      }

      const result = await response.json();

      // Add success message to chat
      setTimeout(() => {
        addBotMessage('✅ Programarea dumneavoastră a fost înregistrată cu succes în sistemul nostru!');
      }, 1000);

    } catch (error) {
      console.error('❌ Error creating appointment:', error);
      // Add error message to chat
      setTimeout(() => {
        addBotMessage('❌ Ne pare rău, a apărut o eroare la înregistrarea programării. Vă rugăm să ne contactați direct pentru a confirma programarea.');
      }, 1000);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setMessages([]);
      setAppointmentData({
        created_at: new Date().toISOString() // Keep created_at timestamp
      });
      setCurrentMessage('');
      setAppointmentContext(null);

      // Clear sessionStorage when modal opens
      sessionStorage.removeItem(appointmentDataStorageKey);
      console.log('🗑️ Cleared appointment data from sessionStorage on modal open');

      // Load appointment context first
      loadAppointmentContext();

      // Start conversation with initial question following the required flow
      setTimeout(() => {
        addBotMessage('Bună ziua! Doriți să faceți o programare la AppName?');
      }, 500);
    }
  }, [isOpen]);

  const addBotMessage = (content: string) => {
    const message: ChatMessage = {
      id: Date.now().toString(),
      type: 'bot',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, message]);
  };

  const addUserMessage = (content: string) => {
    const message: ChatMessage = {
      id: (Date.now() + 1).toString(),
      type: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, message]);
  };

  const getConversationContext = (): string => {
    // Get language from app settings
    const language = settings?.default_language || 'ro';
    
    // Build context with real appointment data if available
    let appointmentDataText = '';
    if (appointmentContext) {
      // Build employee information
      const employeesText = appointmentContext.employees.map(emp => {
        const servicesText = emp.services.map(s =>
          `  - ${s.name} (${s.category}, ${s.duration} min, ${s.price} RON)`
        ).join('\n');

        const availabilityText = emp.availability.map(a =>
          `  - Ziua ${a.dayOfWeek}: ${a.startTime} - ${a.endTime}`
        ).join('\n');

        return `**${emp.name} ${emp.surname}**${emp.specialty ? ` (${emp.specialty})` : ''}
Servicii oferite:
${servicesText}
Disponibilitate săptămânală:
${availabilityText}`;
      }).join('\n\n');

      // Build services information
      const servicesText = appointmentContext.services.map(s =>
        `- ${s.name} (${s.category}, ${s.duration} min, ${s.price} RON)`
      ).join('\n');

      appointmentDataText = `
INFORMAȚII CLINICĂ DISPONIBILE:

ANGAJAȚI ȘI SERVICII:
${employeesText}

TOATE SERVICIILE DISPONIBILE:
${servicesText}

IMPORTANT: Folosește aceste informații reale pentru a sugera servicii și specialiști disponibili.`;
    }

    const languageInstructions = language === 'ro'
      ? `Ești un asistent AI pentru programări la AppName. Rolul tău este să ajuți utilizatorii să programeze întâlniri urmând EXACT acest flux:

FLUX CONVERSAȚIONAL OBLIGATORIU:

1. ÎNTREBARE INIȚIALĂ:
   - Întreabă: "Doriți să faceți o programare?"
   - Dacă răspunde NU: încheie conversația politicos cu "Vă mulțumesc pentru interes! Dacă aveți nevoie de informații, suntem aici pentru dumneavoastră."
   - Dacă răspunde DA: continuă cu pasul 2

2. SUGESTIE SERVICII:
   - Interoghează baza de date și sugerează TOATE serviciile disponibile
   - Prezintă-le SUB FORMĂ DE LISTĂ NUMEROTATĂ clară:
     1. Nume serviciu – Categorie – Durată min – Preț RON
     2. Nume serviciu – Categorie – Durată min – Preț RON
   - Întreabă UTILIZATORUL să introducă NUMĂRUL serviciului ales: "Vă rog introduceți numărul serviciului ales (1-X)."
   - VALIDARE: Acceptă DOAR numere valide din listă. Dacă utilizatorul introduce altceva, răspunde: "Număr invalid. Vă rog alegeți un număr dintre opțiunile disponibile."

3. ALEGERE ANGAJAT:
   - După ce utilizatorul alege serviciul, afișează DOAR angajații care pot presta acel serviciu
   - Prezintă-i SUB FORMĂ DE LISTĂ NUMEROTATĂ:
     1. Dr. Nume Prenume – Specializare
     2. Dr. Nume Prenume – Specializare
   - Întreabă UTILIZATORUL să introducă NUMĂRUL medicului ales: "Vă rog introduceți numărul medicului ales."
   - VALIDARE: Acceptă DOAR numere valide din listă. Dacă utilizatorul introduce altceva, răspunde: "Număr invalid. Vă rog alegeți un număr dintre opțiunile disponibile."

4. VERIFICARE DISPONIBILITATE:
   - După selecția angajatului, verifică și afișează PRIMELE 5 intervale disponibile
   - Prezintă-le SUB FORMĂ DE LISTĂ NUMEROTATĂ cu ZILE REALE:
     1. Luni HH:MM – HH:MM
     2. Luni HH:MM – HH:MM
   - Folosește URMĂTOAREA ZI DE LUNi începând de azi (dacă azi este luni, afișează următoarea luni)
   - Întreabă UTILIZATORUL să introducă NUMĂRUL intervalului ales
   - VALIDARE: Acceptă DOAR numere valide din listă. Dacă utilizatorul introduce altceva, răspunde: "Număr invalid. Vă rog alegeți un număr dintre opțiunile disponibile."

5. DATE PERSONALE:
   - Cere UTILIZATORULUI să introducă:
     * Nume complet
     * Email valid
     * Număr de telefon român (+40 sau 07xx)
   - Validează fiecare dată introdusă

6. VALIDARE ȘI CONFIRMARE:
   - Prezintă un rezumat COMPLET al programării
   - Cere confirmarea explicită a utilizatorului
   - Dacă confirmă: răspunde EXACT "Mulțumim, programarea ta a fost înregistrată!"
   - Dacă nu confirmă: oferă opțiuni pentru modificare

REGULI IMPORTANTE:
- Folosește DOAR informațiile din baza de date furnizate mai jos
- Fii politicos, profesionist și vorbește DOAR în limba română
- Nu inventa servicii, angajați sau disponibilități
- Dacă nu există disponibilitate, sugerează alternative
- Respectă exact fluxul de mai sus - nu sări pași
- Dacă utilizatorul vrea să schimbe date, reîncepe de la pasul corespunzător`
      : `You are an AI assistant for appointments at AppName. Your role is to help users schedule appointments following EXACTLY this flow:

MANDATORY CONVERSATIONAL FLOW:

1. INITIAL QUESTION:
   - Ask: "Would you like to make an appointment?"
   - If they answer NO: politely end the conversation with "Thank you for your interest! If you need information, we're here for you."
   - If they answer YES: continue with step 2

2. SERVICE SUGGESTION:
   - Query the database and suggest ALL available services
   - Present them in a CLEAR NUMBERED LIST:
     1. Service name – Category – Duration min – Price RON
     2. Service name – Category – Duration min – Price RON
   - Ask the USER to enter the NUMBER of the chosen service: "Please enter the number of the service you chose (1-X)."
   - VALIDATION: Accept ONLY valid numbers from the list. If the user enters something else, respond: "Invalid number. Please choose a number from the available options."

3. EMPLOYEE SELECTION:
   - After the user chooses the service, display ONLY employees who can provide that service
   - Present them in a NUMBERED LIST:
     1. Dr. Name Surname – Specialty
     2. Dr. Name Surname – Specialty
   - Ask the USER to enter the NUMBER of the chosen doctor: "Please enter the number of the doctor you chose."
   - VALIDATION: Accept ONLY valid numbers from the list. If the user enters something else, respond: "Invalid number. Please choose a number from the available options."

4. AVAILABILITY CHECK:
   - After selecting the employee, check and display the FIRST 5 available intervals
   - Present them in a NUMBERED LIST with REAL DAYS:
     1. Monday HH:MM – HH:MM
     2. Monday HH:MM – HH:MM
   - Use the NEXT MONDAY starting from today (if today is Monday, show next Monday)
   - Ask the USER to enter the NUMBER of the chosen interval
   - VALIDATION: Accept ONLY valid numbers from the list. If the user enters something else, respond: "Invalid number. Please choose a number from the available options."

5. PERSONAL INFORMATION:
   - Ask the USER to enter:
     * Full name
     * Valid email
     * Phone number (format: +40xxxxxxxxx or 07xxxxxxxx)
   - Validate each entered data

6. VALIDATION AND CONFIRMATION:
   - Present a COMPLETE summary of the appointment
   - Request explicit confirmation from the user
   - If confirmed: respond EXACTLY "Thank you, your appointment has been registered!"
   - If not confirmed: offer options for modification

IMPORTANT RULES:
- Use ONLY the information from the database provided below
- Be polite, professional and speak ONLY in English
- Do not invent services, employees or availability
- If there is no availability, suggest alternatives
- Follow the flow above exactly - do not skip steps
- If the user wants to change data, restart from the corresponding step`;

    const context = `
${languageInstructions}

${appointmentDataText}

${language === 'ro' ? 'INFORMAȚII CURENTE COLECTATE:' : 'CURRENTLY COLLECTED INFORMATION:'}
${Object.keys(appointmentData).length > 0 ? Object.entries(appointmentData).map(([key, value]) => `${key}: ${value}`).join('\n') : (language === 'ro' ? 'Nicio informație colectată încă' : 'No information collected yet')}

${language === 'ro' ? 'CONVERSAȚIE PRECEDENTĂ:' : 'PREVIOUS CONVERSATION:'}
${messages.slice(-4).map(msg => `${msg.type === 'bot' ? (language === 'ro' ? 'Asistent' : 'Assistant') : (language === 'ro' ? 'Utilizator' : 'User')}: ${msg.content}`).join('\n')}
    `.trim();

    return context;
  };

  const updateAppointmentDataFromResponse = (userInput: string, aiResponse: string) => {
    // Special handling for comma-separated input (name, email, phone)
    const commaSeparatedPattern = /^([^,]+),\s*([^,]+),\s*([^,]+)$/;
    const commaMatch = userInput.match(commaSeparatedPattern);
    if (commaMatch) {
      const potentialName = commaMatch[1].trim();
      const potentialEmail = commaMatch[2].trim();
      const potentialPhone = commaMatch[3].trim();

      // Validate and extract name
      if (/^[A-Z][a-z]+\s+[A-Z][a-z]+$/.test(potentialName)) {
        setAppointmentData(prev => {
          return { ...prev, name: potentialName };
        });
      }

      // Validate and extract email
      if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(potentialEmail)) {
        setAppointmentData(prev => {
          return { ...prev, email: potentialEmail };
        });
      }

      // Validate and extract phone
      const cleanPhone = potentialPhone.replace(/[\s\-\(\)]+/g, '');
      if (/^[0-9]{10,}$/.test(cleanPhone)) {
        setAppointmentData(prev => {
          return { ...prev, phone: cleanPhone };
        });
      }

      // If we successfully extracted all data from comma-separated input, return early
      if (potentialName && potentialEmail && potentialPhone) {
        return;
      }
    }

    // Extract numeric selections first (highest priority)
    const numericInput = userInput.trim();

    // Check if input is just a number (for service, doctor, or availability selection)
    if (/^\d+$/.test(numericInput)) {
      const selectedNumber = parseInt(numericInput, 10);
      console.log('🔢 Detected numeric input:', selectedNumber, 'for user input:', userInput.trim());
      console.log('🔢 Numeric input processing started...');

      // Check if we have services available and this number matches a service (only if no service is selected yet)
      if (appointmentContext && appointmentContext.services && selectedNumber >= 1 && selectedNumber <= appointmentContext.services.length && !appointmentData.serviceId) {
        const selectedService = appointmentContext.services[selectedNumber - 1];
        if (selectedService) {
          setAppointmentData(prev => {
            const updated = {
              ...prev,
              service: selectedService.name,
              serviceId: selectedService.id
            };
            return updated;
          });

          return; // Exit early after processing numeric selection
        }
      }

      // Check if we have employees available and this number matches an employee
      // Only process employee selection if we have a service selected but no employee selected yet
      if (appointmentContext && appointmentContext.employees && appointmentData.serviceId && !appointmentData.employeeId) {
        // Filter employees based on selected service if one is selected
        let availableEmployees = appointmentContext.employees;

        if (appointmentData.serviceId) {
          // Convert serviceId to number for comparison
          const serviceIdNum = typeof appointmentData.serviceId === 'string' ? parseInt(appointmentData.serviceId, 10) : appointmentData.serviceId;

          availableEmployees = appointmentContext.employees.filter(employee => {
            const canProvideService = employee.services && employee.services.some(service => {
              const serviceMatch = service.id === serviceIdNum;
              return serviceMatch;
            });

            return canProvideService;
          });
        }

        if (selectedNumber >= 1 && selectedNumber <= availableEmployees.length) {
          const selectedEmployee = availableEmployees[selectedNumber - 1];

          if (selectedEmployee) {
            const newEmployeeId = selectedEmployee.id;
            const newSpecialistName = selectedEmployee.name + ' ' + selectedEmployee.surname;

            setAppointmentData(prev => {
              const updated = {
                ...prev,
                specialist: newSpecialistName,
                employeeId: newEmployeeId
              };
              return updated;
            });

            // Force re-render by updating a dummy state
            setCurrentMessage(prev => prev + ' ');

            return; // Exit early after processing numeric selection
          }
        } else {
          console.log('Employee selection failed - number out of range for filtered employees:', {
            selectedNumber,
            availableEmployeesCount: availableEmployees.length,
            isInRange: selectedNumber >= 1 && selectedNumber <= availableEmployees.length
          });
        }
              } else {
          console.log('Employee selection failed - conditions not met:', {
          hasContext: !!appointmentContext,
          hasEmployees: !!appointmentContext?.employees,
          employeesCount: appointmentContext?.employees?.length || 0,
          hasServiceId: !!appointmentData.serviceId,
          serviceId: appointmentData.serviceId,
          hasEmployeeId: !!appointmentData.employeeId,
          employeeId: appointmentData.employeeId,
          selectedNumber,
          isInRange: selectedNumber >= 1 && selectedNumber <= (appointmentContext?.employees?.length || 0)
        });
      }

      // Check if we have employee selected and this number matches an availability slot
      if (appointmentContext && appointmentContext.employees && appointmentData.employeeId && selectedNumber >= 1 && selectedNumber <= 5) {
        console.log('📅 Processing availability slot selection:', selectedNumber);

        // Find the selected employee to get their availability
        const selectedEmployee = appointmentContext.employees.find(emp => emp.id === appointmentData.employeeId);
        if (selectedEmployee && selectedEmployee.availability && selectedEmployee.availability.length > 0) {
          // Get availability slot (we'll use the first availability entry as base)
          const baseAvailability = selectedEmployee.availability[0];

          if (baseAvailability) {
            // Calculate the actual slot time based on selected number
            // Assuming slots are 1.5 hours each starting from 09:00
            const baseHour = 9; // 09:00
            const slotDuration = 90; // 90 minutes
            const slotIndex = selectedNumber - 1; // Convert to 0-based index

            const startMinutes = baseHour * 60 + (slotIndex * slotDuration);
            const endMinutes = startMinutes + slotDuration;

            const startHour = Math.floor(startMinutes / 60);
            const startMin = startMinutes % 60;
            const endHour = Math.floor(endMinutes / 60);
            const endMin = endMinutes % 60;

            const startTime = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
            const endTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;

            // Get day info for next Monday (1 = Monday)
            const dayInfo = getNextDayOfWeek(1);
            console.log('📅 Calculated next Monday:', {
              dayName: dayInfo.dayName,
              fullDate: dayInfo.fullDate,
              daysUntil: dayInfo.daysUntil
            });

            // Create formatted date string for backend (YYYY-MM-DD HH:MM-HH:MM)
            const dateString = `${dayInfo.fullDate} ${startTime}-${endTime}`;

            setAppointmentData(prev => {
              const updated = {
                ...prev,
                date: dateString,
                startDate: dateString, // Same format as date
                endDate: dateString,   // Same format as date
                created_at: new Date().toISOString()
              };
              return updated;
            });

            return; // Exit early after processing availability selection
          }
        } else {
          console.log('No availability data found for selected employee');
        }
      }

      // If no specific selection matched, pass to AI for validation
    }

    // Extract service information - multiple patterns
    const servicePatterns = [
      /(serviciu|servicii)[^:]*:?\s*([^,\n]+)/i,
      /(alege?|selectez?|vreau)\s+(?:serviciul?)?\s*([^,\n]+)/i,
      /^([^,\n]+)$/i  // If input is just the service name
    ];

    for (const pattern of servicePatterns) {
      const serviceMatch = userInput.match(pattern);
      if (serviceMatch && ((serviceMatch[2] && serviceMatch[2].length > 2) || (serviceMatch[1] && serviceMatch[1].length > 2))) {
        const serviceName = (serviceMatch[2] || serviceMatch[1]).trim();

        // Find service ID from context
        let serviceId: number | undefined = undefined;
        if (appointmentContext) {
          const serviceObj = appointmentContext.services.find(s =>
            s.name.toLowerCase().includes(serviceName.toLowerCase()) ||
            serviceName.toLowerCase().includes(s.name.toLowerCase())
          );
          if (serviceObj) {
            serviceId = serviceObj.id;
          }
        }

        setAppointmentData(prev => {
          return { ...prev, service: serviceName, serviceId };
        });
        break;
      }
    }

    // Extract specialist information - multiple patterns
    const specialistPatterns = [
      /(?:dr\.?|doctor|medic|dentist|specialist)[^:]*:?\s*([^,\n]+)/i,
      /(alege?|selectez?|vreau)\s+(?:pe\s+)?(?:dr\.?|doctor)?\s*([^,\n]+)/i,
      /^([^,\n]+)$/i  // If input is just the specialist name
    ];

    for (const pattern of specialistPatterns) {
      const specialistMatch = userInput.match(pattern);
      if (specialistMatch && ((specialistMatch[2] && specialistMatch[2].length > 2) || (specialistMatch[1] && specialistMatch[1].length > 2))) {
        // Use the appropriate capture group
        const specialistInput = (specialistMatch[2] || specialistMatch[1]).trim();

        // Skip common words that are not specialist names
        const commonWords = ['da', 'nu', 'da', 'nu', 'ok', 'okay', 'bine', 'perfect', 'alege', 'selectez', 'vreau'];
        if (commonWords.includes(specialistInput.toLowerCase()) || specialistInput.length < 3) {
          continue;
        }

        // Find employee from context
        let employeeId: number | undefined = undefined;
        let specialistName = specialistInput;

        if (appointmentContext) {
          // If it's just a first name like "G", try to match with full name
          if (specialistInput.length <= 4) {
            const fullName = appointmentContext.employees.find(emp =>
              emp.name.toLowerCase().startsWith(specialistInput.toLowerCase()) ||
              emp.surname.toLowerCase().startsWith(specialistInput.toLowerCase())
            );
            if (fullName) {
              specialistName = fullName.name + ' ' + fullName.surname;
              employeeId = fullName.id;
            }
          } else {
            // Try to match full name
            const employee = appointmentContext.employees.find(emp =>
              (emp.name + ' ' + emp.surname).toLowerCase().includes(specialistInput.toLowerCase()) ||
              specialistInput.toLowerCase().includes((emp.name + ' ' + emp.surname).toLowerCase())
            );
            if (employee) {
              specialistName = employee.name + ' ' + employee.surname;
              employeeId = employee.id;
            }
          }
        }

        // Only update if we found a valid employee
        if (employeeId) {
          setAppointmentData(prev => {
            const updated = { ...prev, specialist: specialistName, employeeId };
            return updated;
          });
          break;
        }
      }
    }

    // Extract date/time information - multiple patterns
    const datePatterns = [
      /(?:ziua\s+(\d+))[,\s]*(?:ora\s+)?(\d{1,2}:\d{2})/i,
      /(\*?\s*ziua\s+\d+)[,\s]*(?:ora\s+)?(\d{1,2}:\d{2})/i,
      /(ziua\s+\d+)[,\s]*(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/i,
      /(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/i
    ];

    for (const pattern of datePatterns) {
      const dateMatch = userInput.match(pattern);
      if (dateMatch) {
        // For date patterns, construct "Ziua X, HH:MM - HH:MM" format
        let dateStr = '';
        if (dateMatch[1] && dateMatch[2]) {
          // Ziua X, ora HH:MM format
          const dayNum = dateMatch[1];
          const time = dateMatch[2];
          // Calculate end time based on service duration (if available)
          let endTime = time;
          if (appointmentData.service) {
            // Try to extract duration from service name or use default
            const durationMatch = appointmentData.service.match(/(\d+)\s*min/i);
            if (durationMatch) {
              const duration = parseInt(durationMatch[1]);
              const [hours, minutes] = time.split(':').map(Number);
              const totalMinutes = hours * 60 + minutes + duration;
              const endHours = Math.floor(totalMinutes / 60);
              const endMinutes = totalMinutes % 60;
              endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
            }
          }
          // Get day info for the calculated day (use next Monday if dayNum is 0)
          const dayNumParsed = parseInt(dayNum, 10);
          const dayInfo = dayNumParsed === 0 ? getNextDayOfWeek(1) : getDayInfo(dayNumParsed);
          dateStr = `${dayInfo.fullDate} ${time}-${endTime}`;

          // Also update startDate and endDate for debugging
          const startDateFormatted = `${time} ${dayInfo.dayName}`;
          const endDateFormatted = `${endTime} ${dayInfo.dayName}`;

          setAppointmentData(prev => ({
            ...prev,
            date: dateStr,
            startDate: dateStr, // Same format as date
            endDate: dateStr,   // Same format as date
            created_at: new Date().toISOString()
          }));
          return; // Exit early after setting date
        }

        if (dateStr) {
          setAppointmentData(prev => {
            return { ...prev, date: dateStr };
          });
          break;
        }
      }
    }

    // Extract name information - multiple patterns
    const namePatterns = [
      /(nume|prenume)[^:]*:?\s*([^,\n]+)/i,
      /(ma\s+numesc|eu\s+sunt)[^:]*:?\s*([^,\n]+)/i,
      /^([A-Z][a-z]+\s+[A-Z][a-z]+)$/  // Full name pattern
    ];

    for (const pattern of namePatterns) {
      const nameMatch = userInput.match(pattern);
      if (nameMatch && nameMatch[2] && nameMatch[2].length > 3) {
        const name = nameMatch[2].trim();
        setAppointmentData(prev => {
          return { ...prev, name };
        });
        break;
      } else if (nameMatch && nameMatch[1] && nameMatch[1].length > 3 && pattern === namePatterns[2]) {
        const name = nameMatch[1].trim();
        setAppointmentData(prev => {
          return { ...prev, name };
        });
        break;
      }
    }

    // Extract email information
    const emailMatch = userInput.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
    if (emailMatch && emailMatch[1]) {
      const email = emailMatch[1].trim();
      setAppointmentData(prev => {
        return { ...prev, email };
      });
    }

    // Extract phone information - multiple patterns
    const phonePatterns = [
      /(telefon|tel|mobile|nr)[^:]*:?\s*([+0-9\s\-\(\)]+)/i,
      /([+0-9]{10,})/  // Just phone number
    ];

    for (const pattern of phonePatterns) {
      const phoneMatch = userInput.match(pattern);
      if (phoneMatch && phoneMatch[2]) {
        const phone = phoneMatch[2].replace(/[\s\-\(\)]+/g, '');
        if (isValidPhone(phone)) {
          setAppointmentData(prev => {
            return { ...prev, phone };
          });
        }
        break;
      } else if (phoneMatch && phoneMatch[1] && pattern === phonePatterns[1]) {
        const phone = phoneMatch[1].replace(/[\s\-\(\)]+/g, '');
        if (isValidPhone(phone)) {
          setAppointmentData(prev => {
            return { ...prev, phone };
          });
        }
        break;
      }
    }

    // Also extract from AI response if it contains structured data
    // WARNING: This might overwrite user selections! Only extract if data is missing
    if (aiResponse && (!appointmentData.service || !appointmentData.specialist || !appointmentData.employeeId || !appointmentData.serviceId)) {
      // Extract from AI response if it mentions the data and we don't have it yet
      const aiServiceMatch = aiResponse.match(/serviciu[^:]*:?\s*([^,\n]+)/i);
      if (aiServiceMatch && aiServiceMatch[1] && !appointmentData.service) {
        const serviceName = aiServiceMatch[1].trim();

        // Find service ID from context
        let serviceId: number | undefined = undefined;
        if (appointmentContext) {
          const serviceObj = appointmentContext.services.find(s =>
            s.name.toLowerCase().includes(serviceName.toLowerCase()) ||
            serviceName.toLowerCase().includes(s.name.toLowerCase())
          );
          if (serviceObj) {
            serviceId = serviceObj.id;
          }
        }

        setAppointmentData(prev => ({ ...prev, service: serviceName, serviceId }));
      }

      const aiSpecialistMatch = aiResponse.match(/specialist[^:]*:?\s*([^,\n]+)/i);
      if (aiSpecialistMatch && aiSpecialistMatch[1] && !appointmentData.specialist) {
        const specialistName = aiSpecialistMatch[1].trim();

        // Find employee from context
        let employeeId: number | undefined = undefined;
        if (appointmentContext) {
          const employee = appointmentContext.employees.find(emp =>
            (emp.name + ' ' + emp.surname).toLowerCase().includes(specialistName.toLowerCase()) ||
            specialistName.toLowerCase().includes((emp.name + ' ' + emp.surname).toLowerCase())
          );
          if (employee) {
            employeeId = employee.id;
          }
        }

        setAppointmentData(prev => ({ ...prev, specialist: specialistName, employeeId }));
      }
    }

    // Clean and normalize appointment data
    const cleanAppointmentData = () => {
      const cleaned = { ...appointmentData };

      // Clean service name - remove prefixes and extra text
      if (cleaned.service) {
        cleaned.service = cleaned.service
          .replace(/^\*\*\s*/, '') // Remove "** " prefix
          .replace(/\s*\([^)]*\)$/, '') // Remove "(category)" suffix
          .trim();
      }

      // Clean specialist name - remove prefixes
      if (cleaned.specialist) {
        cleaned.specialist = cleaned.specialist
          .replace(/^\*\*\s*/, '') // Remove "** " prefix
          .replace(/^\*\s*/, '') // Remove "* " prefix
          .trim();
      }

      return cleaned;
    };

    const cleanedData = cleanAppointmentData();
    // Check if AI response contains confirmation and create appointment
    if (aiResponse.toLowerCase().includes('programarea') && aiResponse.toLowerCase().includes('înregistrată')) {
      const completeData = getCompleteAppointmentData();

      // Validate that we have all required data
      if (!completeData.serviceId || !completeData.employeeId || !completeData.date) {
        console.error('❌ Missing required data for appointment creation:', {
          serviceId: completeData.serviceId,
          employeeId: completeData.employeeId,
          date: completeData.date
        });
        setTimeout(() => {
          addBotMessage('❌ Ne pare rău, nu am putut colecta toate informațiile necesare pentru programare. Vă rugăm să încercați din nou.');
        }, 1000);
        return;
      }

      // Create appointment in database
      createAppointmentInDatabase(completeData);
      onAppointmentComplete(completeData);
    }
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim()) return;

    const userInput = currentMessage.trim();
    addUserMessage(userInput);
    setCurrentMessage('');
    setIsTyping(true);

    try {
      // Call Gemini API (public endpoint for appointment assistant)
      const response = await fetchWithSchema('/api/ai/appointment-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userInput,
          context: getConversationContext(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get AI response');
      }

      const data = await response.json();

      if (data.success && data.data?.message) {
        addBotMessage(data.data.message);

        // Update appointment data if we detect structured information
        updateAppointmentDataFromResponse(userInput, data.data.message);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      addBotMessage(t('chatbot.error'));
    } finally {
      setIsTyping(false);
    }
  };



  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string): boolean => {
    // Romanian phone number validation
    const phoneRegex = /^(\+40|0)[0-9]{9}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClose = () => {
    setMessages([]);
    setAppointmentData({});
    setCurrentMessage('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl h-[600px] flex flex-col p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-primary" />
              </div>
              <div>
                <DialogTitle>{t('chatbot.title')}</DialogTitle>
                <DialogDescription>
                  {t('chatbot.subtitle')}
                </DialogDescription>
              </div>
            </div>

          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.type === 'bot' && (
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                  )}

                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      message.type === 'user'
                        ? 'bg-primary text-primary-foreground ml-auto'
                        : 'bg-muted'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <span className="text-xs opacity-70 mt-1 block">
                      {message.timestamp.toLocaleTimeString('ro-RO', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  {message.type === 'user' && (
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                  <div className="bg-muted p-3 rounded-lg">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div ref={messagesEndRef} />
          </ScrollArea>

          {/* Current appointment data summary */}
          {Object.keys(appointmentData).length > 0 && (
            <div className="p-4 border-t bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{t('chatbot.appointmentData')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {appointmentData.service && (
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span>{t('chatbot.service')}: {appointmentData.service}</span>
                  </div>
                )}
                {appointmentData.specialist && (
                  <div className="flex items-center gap-1">
                    <UserCheck className="w-3 h-3 text-green-600" />
                    <span>{t('chatbot.specialist')}: {appointmentData.specialist}</span>
                  </div>
                )}
                {appointmentData.name && (
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3 text-green-600" />
                    <span>{t('chatbot.name')}: {appointmentData.name}</span>
                  </div>
                )}
                {appointmentData.email && (
                  <div className="flex items-center gap-1">
                    <Mail className="w-3 h-3 text-green-600" />
                    <span>{t('chatbot.email')}: {appointmentData.email}</span>
                  </div>
                )}
                {appointmentData.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="w-3 h-3 text-green-600" />
                    <span>{t('chatbot.phone')}: {appointmentData.phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chatbot.placeholder')}
                className="flex-1"
disabled={isTyping}
              />
              <Button
                onClick={handleSendMessage}
disabled={!currentMessage.trim() || isTyping}
                size="icon"
              >
                {isTyping ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Debug inputs - remove in production */}
        <div className="hidden">
          <input type="hidden" name="debug_serviceId" value={appointmentData.serviceId || ''} />
          <input type="hidden" name="debug_employeeId" value={appointmentData.employeeId || ''} />
          <input type="hidden" name="debug_date" value={appointmentData.date || ''} />
          <input type="hidden" name="debug_service" value={appointmentData.service || ''} />
          <input type="hidden" name="debug_specialist" value={appointmentData.specialist || ''} />
          <input type="hidden" name="debug_start_date" value={appointmentData.startDate || ''} />
          <input type="hidden" name="debug_end_date" value={appointmentData.endDate || ''} />
          <input type="hidden" name="debug_created_at" value={appointmentData.created_at || ''} />
          <input type="hidden" name="debug_name" value={appointmentData.name || ''} />
          <input type="hidden" name="debug_email" value={appointmentData.email || ''} />
          <input type="hidden" name="debug_phone" value={appointmentData.phone || ''} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatbotModal;
